/**
 * API Configuration
 * 
 * Provides the API base URL for frontend-backend communication.
 * Supports both combined deployment (same origin) and separated deployment (cross-origin).
 * 
 * In development: Uses Vite proxy (relative URLs work)
 * In production combined: Relative URLs work (same origin)
 * In production separated: Uses VITE_API_URL environment variable
 */

// Declare the global constant injected by Vite
declare const __API_URL__: string;

/**
 * Get the base URL for API calls.
 * Returns empty string for same-origin (combined) deployment,
 * or the full URL for cross-origin (separated) deployment.
 */
export function getApiBaseUrl(): string {
  // Check for Vite's injected constant first
  if (typeof __API_URL__ !== 'undefined' && __API_URL__) {
    return __API_URL__;
  }
  
  // Fallback: check import.meta.env (for older Vite versions)
  if (import.meta.env?.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // Default: same origin (combined deployment)
  return '';
}

/**
 * Build full API URL from a path.
 * Handles both absolute paths (/api/...) and relative paths (api/...).
 */
export function buildApiUrl(path: string): string {
  const baseUrl = getApiBaseUrl();
  
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  // If we have a base URL, combine them
  if (baseUrl) {
    // Remove trailing slash from base URL if present
    const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    return `${cleanBase}${normalizedPath}`;
  }
  
  // Same origin - just use the path
  return normalizedPath;
}

/**
 * Check if we're running in separated deployment mode (cross-origin)
 */
export function isCrossOriginApi(): boolean {
  return getApiBaseUrl() !== '';
}

/**
 * Build full URL for media assets (logos, favicons, uploaded images).
 * 
 * Handles:
 * - /api/media/... paths -> full backend URL
 * - https://... URLs -> returned as-is
 * - Empty/null values -> returned as-is
 */
export function buildMediaUrl(path: string | null | undefined): string {
  // Handle empty/null values
  if (!path) return '';
  
  // Already an absolute URL (http/https/data) - return as-is
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) {
    return path;
  }
  
  // API media path - build full URL
  if (path.startsWith('/api/media/')) {
    return buildApiUrl(path);
  }
  
  // Other paths (like local assets) - return as-is
  return path;
}

// Export for testing/debugging
export const API_CONFIG = {
  getBaseUrl: getApiBaseUrl,
  buildUrl: buildApiUrl,
  buildMediaUrl: buildMediaUrl,
  isCrossOrigin: isCrossOriginApi,
};
